import React, { useState, useRef, Fragment, useEffect, useMemo } from 'react';
import { API_BASE, authFetch } from '../lib/rpc';
import { useToast } from '@/components/ui/ToastProvider';
import { useDialog } from '@/components/ui/DialogProvider';
import { RefreshCw, FolderUp, Download } from 'lucide-react';
interface FileExplorerProps {
  serverId: string;
  isLight?: boolean;
  readOnly?: boolean;
  baseDirectory?: string;
  templates?: any[];
  onClose: () => void;
  onTranscribeTriggered?: () => void;
  onTranscribeRequest?: (filename: string, fileKey: string) => void;
  apiAuthQueryParams?: string;
}

export function FileExplorer({ serverId, isLight = false, readOnly = false, baseDirectory = '', templates = [], onClose, onTranscribeTriggered, onTranscribeRequest, apiAuthQueryParams }: FileExplorerProps) {
    const { showToast } = useToast();
    const { alert, confirm, prompt } = useDialog();
  const [explorerPrefix, setExplorerPrefix] = useState(baseDirectory);
  const [explorerItems, setExplorerItems] = useState<{ directories: string[], files: any[] }>({ directories: [], files: [] });
  const [explorerLoading, setExplorerLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [sortBy, setSortBy] = useState<'name' | 'size' | 'type' | 'date'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const [playingFileKey, setPlayingFileKey] = useState<string | null>(null);
  const [audioProgress, setAudioProgress] = useState(0);
  const [isBuffering, setIsBuffering] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const [transcribeFileKey, setTranscribeFileKey] = useState<string | null>(null);
  const [transcribeFileHasText, setTranscribeFileHasText] = useState<boolean>(false);
  const [transcriptionText, setTranscriptionText] = useState<string | null>(null);
  const [transcriptionTextLoading, setTranscriptionTextLoading] = useState(false);
  const [showReTranscribe, setShowReTranscribe] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [transcriptionSubmitting, setTranscriptionSubmitting] = useState(false);
  const [expandedFileKey, setExpandedFileKey] = useState<string | null>(null);

  useEffect(() => {
    if (transcribeFileKey && transcribeFileHasText && !showReTranscribe) {
        setTranscriptionTextLoading(true);
        setTranscriptionText(null);
        
        const txtKey = transcribeFileKey.replace(/\.(mp3|wav)$/, '.txt');
        const txtUrl = `${API_BASE}api/freepbx-servers/file?serverId=${serverId}&key=${encodeURIComponent(txtKey)}${apiAuthQueryParams ? `&${apiAuthQueryParams}` : ''}`;
        
        authFetch(txtUrl)
            .then(res => res.text())
            .then(text => {
                setTranscriptionText(text);
                setTranscriptionTextLoading(false);
            })
            .catch(err => {
                console.error(err);
                setTranscriptionText("Failed to load transcription.");
                setTranscriptionTextLoading(false);
            });
    }
  }, [transcribeFileKey, transcribeFileHasText, showReTranscribe, serverId]);

  const loadExplorer = async (prefix: string, search: string = '') => {
    setExplorerLoading(true);
    try {
        let url = `${API_BASE}api/freepbx-servers/${serverId}/files?prefix=${encodeURIComponent(prefix)}`;
        if (search) url += `&search=${encodeURIComponent(search)}`;
        if (apiAuthQueryParams) url += `&${apiAuthQueryParams}`;
        const res = await authFetch(url);
        const data = await res.json();
        if (data.success) {
            setExplorerItems({ directories: data.directories || [], files: data.files || [] });
            setExplorerPrefix(prefix);
            setError(null);
        } else {
            setError(data.error || 'Failed to load files');
        }
    } catch (e: any) {
        setError(e.message);
    } finally {
        setExplorerLoading(false);
    }
  };

  useEffect(() => {
    if (serverId) {
        loadExplorer(baseDirectory);
    }
  }, [serverId, baseDirectory]);

  const handleSort = (col: 'name' | 'size' | 'type' | 'date') => {
      if (sortBy === col) {
          setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
      } else {
          setSortBy(col);
          setSortDir('asc');
      }
  };

  const sortedFiles = useMemo(() => {
      return [...explorerItems.files].sort((a, b) => {
          let valA: any = a.key;
          let valB: any = b.key;
          
          if (sortBy === 'name') {
              valA = a.key.split('/').pop()?.toLowerCase() || '';
              valB = b.key.split('/').pop()?.toLowerCase() || '';
          } else if (sortBy === 'size') {
              valA = a.size;
              valB = b.size;
          } else if (sortBy === 'type') {
              valA = a.key.split('.').pop()?.toLowerCase() || '';
              valB = b.key.split('.').pop()?.toLowerCase() || '';
          } else if (sortBy === 'date') {
              valA = new Date(a.uploaded).getTime();
              valB = new Date(b.uploaded).getTime();
          }
          
          if (valA < valB) return sortDir === 'asc' ? -1 : 1;
          if (valA > valB) return sortDir === 'asc' ? 1 : -1;
          return 0;
      });
  }, [explorerItems.files, sortBy, sortDir]);

  const groupedFiles = useMemo(() => {
      const result: { file: any, isChild: boolean, hasTranscription: boolean }[] = [];
      const processedKeys = new Set<string>();

      for (const file of sortedFiles) {
          if (processedKeys.has(file.key)) continue;

          const isAudio = file.key.endsWith('.mp3') || file.key.endsWith('.wav');
          if (isAudio) {
              const baseKey = file.key.substring(0, file.key.lastIndexOf('.'));
              const txtKey = baseKey + '.txt';
              const txtFile = sortedFiles.find(f => f.key === txtKey);
              
              result.push({ file, isChild: false, hasTranscription: !!txtFile });
              processedKeys.add(file.key);
              
              if (txtFile) {
                  processedKeys.add(txtKey);
              }
          } else {
              if (file.key.endsWith('.txt')) {
                  const baseKey = file.key.substring(0, file.key.length - 4);
                  const parentAudioKeyMp3 = baseKey + '.mp3';
                  const parentAudioKeyWav = baseKey + '.wav';
                  const hasParent = sortedFiles.some(f => f.key === parentAudioKeyMp3 || f.key === parentAudioKeyWav);
                  
                  if (hasParent) {
                      continue;
                  }
              }
              result.push({ file, isChild: false, hasTranscription: false });
              processedKeys.add(file.key);
          }
      }
      return result;
  }, [sortedFiles]);

  const sortedDirectories = useMemo(() => {
      const dirs = [...explorerItems.directories];
      if (sortBy === 'name') {
          dirs.sort((a, b) => {
              const valA = a.toLowerCase();
              const valB = b.toLowerCase();
              if (valA < valB) return sortDir === 'asc' ? -1 : 1;
              if (valA > valB) return sortDir === 'asc' ? 1 : -1;
              return 0;
          });
      }
      return dirs;
  }, [explorerItems.directories, sortBy, sortDir]);

  const togglePlay = (fileKey: string) => {
      if (playingFileKey === fileKey) {
          audioRef.current?.pause();
          setPlayingFileKey(null);
          setIsBuffering(false);
      } else {
          setPlayingFileKey(fileKey);
          setAudioProgress(0);
          setIsBuffering(true);
          setTimeout(() => {
              if (audioRef.current) {
                  audioRef.current.play().catch(err => {
                      console.error("Audio playback error:", err);
                      setIsBuffering(false);
                  });
              }
          }, 0);
      }
  };

  const deleteFile = async (key: string) => {
      if (!(await confirm('Are you sure you want to delete this file? This cannot be undone.'))) return;
      try {
          const res = await authFetch(`${API_BASE}api/freepbx-servers/${serverId}/files${apiAuthQueryParams ? `?${apiAuthQueryParams}` : ''}`, {
              method: 'DELETE',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ key })
          });
          if (res.ok) {
              loadExplorer(explorerPrefix);
          } else {
              const err = await res.json();
              showToast(err.error || 'Delete failed', 'error');
          }
      } catch (err: any) {
          showToast('Delete failed: ' + err.message, 'error');
      }
  };

  const deleteDirectory = async (dirPrefix: string) => {
      if (!(await confirm('Are you sure you want to delete this ENTIRE folder and all its contents? This cannot be undone.'))) return;
      try {
          const res = await authFetch(`${API_BASE}api/freepbx-servers/${serverId}/files${apiAuthQueryParams ? `?${apiAuthQueryParams}` : ''}`, {
              method: 'DELETE',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ prefix: dirPrefix })
          });
          if (res.ok) {
              loadExplorer(explorerPrefix);
          } else {
              const err = await res.json();
              showToast(err.error || 'Delete failed', 'error');
          }
      } catch (err: any) {
          showToast('Delete failed: ' + err.message, 'error');
      }
  };

  const uploadFile = async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('prefix', explorerPrefix);
      try {
          const res = await authFetch(`${API_BASE}api/freepbx-servers/${serverId}/files/upload${apiAuthQueryParams ? `?${apiAuthQueryParams}` : ''}`, {
              method: 'POST',
              body: formData
          });
          if (res.ok) {
              loadExplorer(explorerPrefix);
          } else {
              const err = await res.json();
              showToast(err.error || 'Upload failed', 'error');
          }
      } catch (err: any) {
          showToast('Upload failed: ' + err.message, 'error');
      }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
          uploadFile(e.target.files[0]);
      }
  };

  const getVisualPathParts = () => {
      let path = explorerPrefix;
      if (baseDirectory && path.startsWith(baseDirectory)) {
          path = path.substring(baseDirectory.length);
      }
      return path.split('/').filter(p => p);
  };

  const navigateUp = () => {
      const parts = explorerPrefix.split('/').filter(p => p);
      parts.pop();
      let upPrefix = parts.length > 0 ? parts.join('/') + '/' : '';
      if (baseDirectory && upPrefix.length < baseDirectory.length) {
          upPrefix = baseDirectory;
      }
      loadExplorer(upPrefix);
  };

  const canNavigateUp = () => {
      if (explorerPrefix === baseDirectory) return false;
      return explorerPrefix.length > baseDirectory.length;
  };

  return (
    <>
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100000, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
         <div onClick={() => setExpandedFileKey(null)} style={{ background: isLight ? '#ffffff' : '#1e1e1e', color: isLight ? '#09090b' : 'white', borderRadius: '12px', width: '100%', maxWidth: '800px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 40px rgba(0,0,0,0.5)', border: isLight ? '1px solid rgba(0,0,0,0.1)' : '1px solid rgba(255,255,255,0.1)' }}>
            
            <div style={{ padding: '1.5rem 2rem', borderBottom: isLight ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
               <h3 style={{ margin: 0, color: isLight ? '#09090b' : 'white', fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                   <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>
                   {readOnly ? 'Recordings Explorer' : 'File Explorer'}
               </h3>
               <button onClick={onClose} style={{ background: 'none', border: 'none', color: isLight ? '#6b7280' : 'var(--text-muted)', cursor: 'pointer', display: 'flex' }} title="Close">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
               </button>
            </div>

            {error && (
                <div style={{ padding: '1rem 2rem', background: '#fef2f2', color: '#991b1b', borderBottom: '1px solid #fecaca', fontSize: '0.9rem' }}>
                    {error}
                </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', background: isLight ? '#f9fafb' : 'rgba(0,0,0,0.2)', borderBottom: isLight ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.05)' }}>
                {/* Controls Row */}
                <div style={{ padding: '1rem 2rem 0.5rem 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        {(baseDirectory === 'audio/recordings/' || baseDirectory === 'audio/') && (
                            <button 
                                onClick={() => {
                                    const today = new Date();
                                    const yyyy = today.getFullYear();
                                    const mm = (today.getMonth() + 1).toString().padStart(2, '0');
                                    const dd = today.getDate().toString().padStart(2, '0');
                                    const todayPath = `audio/recordings/${yyyy}/${mm}/${dd}/`;
                                    loadExplorer(todayPath);
                                }} 
                                style={{ background: 'var(--primary)', border: 'none', color: 'white', padding: '0.4rem 0.8rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 500 }}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                                Today's Recordings
                            </button>
                        )}
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        {!readOnly && (
                        <label style={{ background: 'var(--primary)', color: 'white', padding: '0.4rem 0.8rem', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 500 }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="17 8 12 3 7 8"></polyline><line x1="12" y1="3" x2="12" y2="15"></line></svg>
                            Upload File
                            <input type="file" style={{ display: 'none' }} onChange={handleFileUpload} />
                        </label>
                        )}
                        {canNavigateUp() && !searchQuery && (
                            <button onClick={navigateUp} style={{ background: 'transparent', border: isLight ? '1px solid #d1d5db' : '1px solid var(--border-light)', color: isLight ? '#4b5563' : 'white', width: '32px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Up Directory">
                                <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', width: '16px', height: '16px', minWidth: '16px' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isLight ? '#4b5563' : 'white'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-8l-2-2z"></path><path d="M12 10v6"></path><path d="M9 13l3-3 3 3"></path></svg>
                            </button>
                        )}
                        <button onClick={() => loadExplorer(explorerPrefix, searchQuery)} style={{ background: 'transparent', border: isLight ? '1px solid #d1d5db' : '1px solid var(--border-light)', color: isLight ? '#4b5563' : 'white', width: '32px', height: '32px', borderRadius: '4px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Refresh Directory">
                            <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', width: '14px', height: '14px', minWidth: '14px' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={isLight ? '#4b5563' : 'white'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/></svg>
                        </button>
                        <div style={{ display: 'flex', alignItems: 'center', background: isLight ? 'white' : 'rgba(0,0,0,0.3)', border: isLight ? '1px solid #d1d5db' : '1px solid var(--border-light)', borderRadius: '4px', padding: '0.2rem 0.5rem', height: '32px' }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
                            <input 
                                type="text" 
                                placeholder="Search subfolders..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && loadExplorer(explorerPrefix, searchQuery)}
                                style={{ background: 'transparent', border: 'none', color: isLight ? '#09090b' : 'white', padding: '0.2rem 0.5rem', outline: 'none', fontSize: '0.85rem', width: '150px' }}
                            />
                            {searchQuery && (
                                <button onClick={() => { setSearchQuery(''); loadExplorer(explorerPrefix, ''); }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: '0 0.2rem', display: 'flex', alignItems: 'center' }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Breadcrumbs Row */}
                <div style={{ padding: '0.5rem 2rem 1rem 2rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: isLight ? '#6b7280' : 'var(--text-muted)', fontSize: '0.9rem', overflowX: 'auto', whiteSpace: 'nowrap' }}>
                    <span style={{ cursor: 'pointer', color: explorerPrefix === baseDirectory ? (isLight ? '#09090b' : 'white') : 'var(--primary)', fontWeight: 500 }} onClick={() => loadExplorer(baseDirectory)}>Home</span>
                    {getVisualPathParts().map((part, i, arr) => {
                        const prefixToHere = baseDirectory + arr.slice(0, i + 1).join('/') + '/';
                        const isLast = i === arr.length - 1;
                        return (
                            <span key={prefixToHere} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span>/</span>
                                <span style={{ cursor: isLast ? 'default' : 'pointer', color: isLast ? (isLight ? '#09090b' : 'white') : 'var(--primary)' }} onClick={() => !isLast && loadExplorer(prefixToHere)}>
                                    {part}
                                </span>
                            </span>
                        );
                    })}
                </div>
            </div>
            
            {transcribeFileKey && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 100000, background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
                    <div style={{ background: '#1e1e1e', borderRadius: '12px', width: '100%', maxWidth: transcribeFileHasText && !showReTranscribe ? '800px' : '500px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 40px rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.1)' }}>
                        <div style={{ padding: '1.5rem 2rem', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0, color: 'white', fontSize: '1.25rem' }}>{transcribeFileHasText && !showReTranscribe ? 'Transcription: ' + transcribeFileKey.split('/').pop() : 'Start Transcription'}</h3>
                            <button onClick={() => { setTranscribeFileKey(null); setSelectedTemplateId(''); setShowReTranscribe(false); }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex' }}>
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                            </button>
                        </div>
                        <div style={{ padding: '1.5rem 2rem', overflowY: 'auto', flex: 1 }}>
                            {transcribeFileHasText && !showReTranscribe ? (
                                <>
                                    {transcriptionTextLoading ? (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100px', color: 'var(--text-muted)' }}>Loading transcription...</div>
                                    ) : (
                                        <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6', fontSize: '1.05rem', color: '#e5e7eb', fontFamily: 'system-ui, sans-serif' }}>
                                            {transcriptionText || 'No transcription text available.'}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <p style={{ margin: '0 0 1.5rem 0', color: 'var(--text-muted)', fontSize: '0.95rem' }}>Select a template to transcribe <strong>{transcribeFileKey.split('/').pop()}</strong>.</p>
                                    <label style={{ display: 'block', color: 'var(--text-muted)', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Template</label>
                                    <select 
                                        value={selectedTemplateId} 
                                        onChange={e => setSelectedTemplateId(e.target.value)}
                                        style={{ width: '100%', padding: '0.75rem 1rem', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', borderRadius: '6px', fontSize: '1rem', outline: 'none', marginBottom: '1rem' }}
                                    >
                                        <option value="">-- Select Template --</option>
                                        {templates.map(t => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                    </select>
                                </>
                            )}
                        </div>
                        <div style={{ padding: '1.5rem 2rem', borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'flex-end', gap: '1rem', background: 'rgba(0,0,0,0.1)', borderBottomLeftRadius: '12px', borderBottomRightRadius: '12px' }}>
                            {transcribeFileHasText && !showReTranscribe ? (
                                <>
                                    <button 
                                        onClick={() => setShowReTranscribe(true)}
                                        style={{ padding: '0.6rem 1.5rem', background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}
                                    >
                                        Re-transcribe
                                    </button>
                                    <button 
                                        onClick={() => { setTranscribeFileKey(null); setShowReTranscribe(false); }}
                                        style={{ padding: '0.6rem 1.5rem', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}
                                    >
                                        Close
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button 
                                        onClick={() => {
                                            if (transcribeFileHasText) setShowReTranscribe(false);
                                            else { setTranscribeFileKey(null); setSelectedTemplateId(''); }
                                        }}
                                        style={{ padding: '0.6rem 1.5rem', background: 'transparent', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        onClick={async () => {
                                            if (!selectedTemplateId) {
                                                showToast("Please select a template", 'info');
                                                return;
                                            }
                                            setTranscriptionSubmitting(true);
                                            try {
                                                const res = await authFetch(API_BASE + 'api/freepbx-transcription-jobs/trigger' + (apiAuthQueryParams ? `?${apiAuthQueryParams}` : ''), {
                                                    method: 'POST',
                                                    headers: { 'Content-Type': 'application/json' },
                                                    body: JSON.stringify({ r2Key: `pbx-${serverId}/${transcribeFileKey}`, templateId: selectedTemplateId })
                                                });
                                                const data = await res.json();
                                                if (data.success) {
                                                    showToast("Transcription job triggered successfully!", 'success');
                                                    setTranscribeFileKey(null);
                                                    setSelectedTemplateId('');
                                                    setShowReTranscribe(false);
                                                    if (onTranscribeTriggered) {
                                                        onTranscribeTriggered();
                                                    }
                                                } else {
                                                    showToast(data.error || "Failed to trigger transcription", 'error');
                                                }
                                            } catch (err: any) {
                                                showToast(err.message, 'info');
                                            } finally {
                                                setTranscriptionSubmitting(false);
                                            }
                                        }}
                                        disabled={transcriptionSubmitting || !selectedTemplateId}
                                        style={{ padding: '0.6rem 1.5rem', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: '4px', cursor: (transcriptionSubmitting || !selectedTemplateId) ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: (transcriptionSubmitting || !selectedTemplateId) ? 0.5 : 1 }}
                                    >
                                        {transcriptionSubmitting ? 'Starting...' : 'Start Transcription'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            <div style={{ padding: '0', display: 'flex', flexDirection: 'column', overflowY: 'auto', flex: 1 }}>
               {explorerLoading ? (
                   <div style={{ textAlign: 'center', color: isLight ? '#6b7280' : 'var(--text-muted)', padding: '3rem 0' }}>Loading storage...</div>
               ) : explorerItems.directories.length === 0 && explorerItems.files.length === 0 ? (
                   <div style={{ textAlign: 'center', color: isLight ? '#6b7280' : 'var(--text-muted)', padding: '3rem 0' }}>This directory is empty.</div>
               ) : (
                   <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                       <thead>
                           <tr style={{ background: isLight ? 'rgba(0,0,0,0.02)' : 'rgba(0,0,0,0.3)', borderBottom: isLight ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.05)' }}>
                               <th style={{ padding: '0.75rem 1rem', paddingRight: '0.5rem', width: '40px' }}></th>
                               <th style={{ padding: '0.75rem 1rem', paddingLeft: '0.5rem', fontWeight: 600, fontSize: '0.85rem', color: isLight ? '#4b5563' : '#9ca3af', cursor: 'pointer', userSelect: 'none', width: '100%' }} onClick={() => handleSort('name')}>
                                   Name {sortBy === 'name' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                               </th>
                               <th style={{ padding: '0.75rem 1rem', fontWeight: 600, fontSize: '0.85rem', color: isLight ? '#4b5563' : '#9ca3af', textAlign: 'center', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('size')}>
                                   Size {sortBy === 'size' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                               </th>
                               <th style={{ padding: '0.75rem 1rem', fontWeight: 600, fontSize: '0.85rem', color: isLight ? '#4b5563' : '#9ca3af', textAlign: 'center', minWidth: '90px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('date')}>
                                   Uploaded {sortBy === 'date' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                               </th>
                               <th style={{ padding: '0.75rem 1rem', width: '100px', fontWeight: 600, fontSize: '0.85rem', color: isLight ? '#4b5563' : '#9ca3af', textAlign: 'center', userSelect: 'none' }}>Actions</th>
                           </tr>
                       </thead>
                       <tbody>
                           {sortedDirectories.map((dir) => (
                               <tr key={dir} style={{ borderBottom: isLight ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.05)', cursor: 'pointer' }} onClick={() => loadExplorer(dir)}>
                                   <td style={{ padding: '0.75rem 1rem', paddingRight: '0.5rem', width: '40px', textAlign: 'center', color: isLight ? '#09090b' : 'white' }}>
                                       <svg style={{ display: 'inline-block', flexShrink: 0, minWidth: '18px' }} width="18" height="18" viewBox="0 0 24 24" fill="var(--primary)" fillOpacity="0.2" stroke="var(--primary)" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"></path></svg>
                                   </td>
                                   <td style={{ padding: '0.75rem 1rem', paddingLeft: '0.5rem', color: isLight ? '#09090b' : 'white', maxWidth: 0, width: '100%' }} title={dir.substring(explorerPrefix.length).replace('/', '')}>
                                        <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {dir.substring(explorerPrefix.length).replace('/', '')}
                                        </div>
                                   </td>
                                   <td style={{ padding: '0.75rem 1rem', color: isLight ? '#6b7280' : 'var(--text-muted)', textAlign: 'right', whiteSpace: 'nowrap' }}>--</td>
                                   <td style={{ padding: '0.75rem 1rem', color: isLight ? '#6b7280' : 'var(--text-muted)', textAlign: 'right' }}>--</td>
                                   <td style={{ padding: '0.75rem 1rem', width: '100px', textAlign: 'right', verticalAlign: 'middle' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.5rem' }}>
                                            {!readOnly && (
                                            <span onClick={(e) => { e.stopPropagation(); deleteDirectory(dir); }} title="Delete Folder" style={{ cursor: 'pointer', opacity: 0.8, color: '#ef4444', display: 'inline-flex' }}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path></svg>
                                            </span>
                                            )}
                                        </div>
                                    </td>
                               </tr>
                           ))}
                           {groupedFiles.map(({ file, isChild, hasTranscription }) => {
                                const isAudio = file.key.endsWith('.mp3') || file.key.endsWith('.wav');
                                const fileUrl = `${API_BASE}api/freepbx-servers/${serverId}/files/download?key=${encodeURIComponent(file.key)}${apiAuthQueryParams ? `&${apiAuthQueryParams}` : ''}`;
                                const isPlaying = playingFileKey === fileUrl;
                                const uploadDate = file.uploaded ? new Date(file.uploaded).toLocaleString().replace(' AM', '\u00A0AM').replace(' PM', '\u00A0PM') : '--';
                                return (
                                <Fragment key={file.key}>
                                <tr style={{ background: isChild ? (isLight ? 'rgba(0,0,0,0.02)' : 'rgba(0,0,0,0.2)') : 'transparent', borderBottom: isPlaying ? 'none' : (isLight ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.05)') }}>
                                    <td onClick={() => window.open(fileUrl, '_blank')} style={{ padding: '0.75rem 1rem', paddingRight: '0.5rem', paddingLeft: isChild ? '2.5rem' : '1rem', width: '40px', textAlign: 'center', cursor: 'pointer', position: 'relative', color: isLight ? '#374151' : '#e5e7eb' }}>
                                        {isChild && (
                                            <div style={{ position: 'absolute', left: '1.25rem', top: '-0.75rem', bottom: '50%', width: '0.75rem', borderLeft: isLight ? '2px solid rgba(0,0,0,0.15)' : '2px solid rgba(255,255,255,0.15)', borderBottom: isLight ? '2px solid rgba(0,0,0,0.15)' : '2px solid rgba(255,255,255,0.15)', borderBottomLeftRadius: '6px' }}></div>
                                        )}
                                        {isAudio ? (
                                            <svg style={{ display: 'inline-block', flexShrink: 0, minWidth: '20px' }} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18V5l12-2v13"></path><circle cx="6" cy="18" r="3" fill="currentColor"></circle><circle cx="18" cy="16" r="3" fill="currentColor"></circle></svg>
                                        ) : file.key.endsWith('.txt') ? (
                                            <svg style={{ display: 'inline-block', flexShrink: 0, minWidth: '18px' }} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                                        ) : (
                                            <svg style={{ display: 'inline-block', flexShrink: 0, minWidth: '18px' }} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path><polyline points="13 2 13 9 20 9"></polyline></svg>
                                        )}
                                   </td>
                                   <td onClick={(e) => { e.stopPropagation(); setExpandedFileKey(expandedFileKey === file.key ? null : file.key); }} style={{ padding: '0.75rem 1rem', paddingLeft: '0.5rem', color: isLight ? '#374151' : '#e5e7eb', cursor: 'pointer', maxWidth: 0, width: '100%' }} title={file.key.substring(explorerPrefix.length)}>
                                         <div style={{ whiteSpace: expandedFileKey === file.key ? 'normal' : 'nowrap', wordBreak: expandedFileKey === file.key ? 'break-all' : 'normal', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                             {file.key.substring(explorerPrefix.length)}
                                         </div>
                                    </td>
                                   <td style={{ padding: '0.75rem 1rem', color: isLight ? '#6b7280' : 'var(--text-muted)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                       {file.size / 1024 >= 1000 ? `${Math.round(file.size / 1024 / 1024)} MB` : `${Math.round(file.size / 1024)} KB`}
                                   </td>
                                   <td style={{ padding: '0.75rem 1rem', color: isLight ? '#6b7280' : 'var(--text-muted)', textAlign: 'right', fontSize: '0.85rem' }}>
                                       {uploadDate}
                                   </td>
                                   <td style={{ padding: '0.75rem 1rem', width: '100px', textAlign: 'right', verticalAlign: 'middle' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.5rem' }}>
                                            {isAudio && (
                                                <span onClick={(e) => { e.stopPropagation(); togglePlay(fileUrl); }} title={isPlaying ? "Stop" : "Play"} style={{ cursor: 'pointer', color: isPlaying ? '#ef4444' : '#10b981', display: 'inline-flex', padding: '0.2rem' }}>
                                                    {isPlaying ? (
                                                        isBuffering ? (
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}>
                                                                <line x1="12" y1="2" x2="12" y2="6"></line><line x1="12" y1="18" x2="12" y2="22"></line><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line><line x1="2" y1="12" x2="6" y2="12"></line><line x1="18" y1="12" x2="22" y2="12"></line><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
                                                            </svg>
                                                        ) : (
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2"><rect x="6" y="4" width="4" height="16"></rect><rect x="14" y="4" width="4" height="16"></rect></svg>
                                                        )
                                                    ) : (
                                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                                                    )}
                                                </span>
                                            )}
                                            <a href={fileUrl} target="_blank" rel="noreferrer" title="Download File" style={{ cursor: 'pointer', opacity: 0.8, color: '#3b82f6', display: 'inline-flex' }} onClick={(e) => e.stopPropagation()}>
                                                 <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', width: '16px', height: '16px', minWidth: '16px' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                                             </a>
                                             {(file.key.endsWith('.mp3') || file.key.endsWith('.wav')) && (
                                             <span onClick={() => {
                                                 if (onTranscribeRequest) onTranscribeRequest(file.key.split('/').pop() || '', file.key);
                                                 else {
                                                     setTranscribeFileKey(file.key);
                                                     setTranscribeFileHasText(hasTranscription);
                                                     setShowReTranscribe(!hasTranscription);
                                                 }
                                             }} title={hasTranscription ? "Transcription Available / Re-transcribe" : "Transcribe Audio"} style={{ cursor: 'pointer', opacity: 0.8, color: hasTranscription ? '#10b981' : '#8b5cf6', display: 'inline-flex' }}>
                                                 <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', width: '16px', height: '16px', minWidth: '16px' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={hasTranscription ? '#10b981' : '#8b5cf6'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"></path><path d="M19 10v2a7 7 0 0 1-14 0v-2"></path><line x1="12" y1="19" x2="12" y2="23"></line><line x1="8" y1="23" x2="16" y2="23"></line></svg>
                                             </span>
                                             )}
                                            {!readOnly && (
                                            <span onClick={() => deleteFile(file.key)} title="Delete File" style={{ cursor: 'pointer', opacity: 0.8, color: '#ef4444', display: 'inline-flex' }}>
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path></svg>
                                            </span>
                                            )}
                                        </div>
                                    </td>
                               </tr>
                               {isPlaying && (
                                   <tr style={{ borderBottom: isLight ? '1px solid rgba(0,0,0,0.05)' : '1px solid rgba(255,255,255,0.05)', background: isLight ? 'rgba(0,0,0,0.02)' : 'rgba(0,0,0,0.2)' }}>
                                       <td colSpan={5} style={{ padding: '0 2rem 1rem 2rem' }}>
                                           <div style={{ height: '4px', background: isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)', borderRadius: '2px', overflow: 'hidden', width: '100%' }}>
                                               <div style={{ height: '100%', background: 'var(--primary)', width: `${audioProgress}%`, transition: 'width 0.1s linear' }} />
                                           </div>
                                       </td>
                                   </tr>
                               )}
                               </Fragment>
                               );
                           })}
                       </tbody>
                   </table>
               )}
            </div>
         </div>
      </div>
      <audio 
           ref={audioRef} 
           src={playingFileKey || undefined} 
           onWaiting={() => setIsBuffering(true)}
           onPlaying={() => setIsBuffering(false)}
           onCanPlay={() => setIsBuffering(false)}
           onTimeUpdate={(e) => {
               const target = e.target as HTMLAudioElement;
               if (target.duration) {
                   setAudioProgress((target.currentTime / target.duration) * 100);
               }
           }}
           onEnded={() => {
               setPlayingFileKey(null);
               setAudioProgress(0);
               setIsBuffering(false);
           }}
           style={{ display: 'none' }}
      />
    </>
  );
}
